import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export type Permissions = {
  is_super_admin: boolean;
  can_manage_home: boolean;
  can_manage_community: boolean;
  can_manage_volunteering: boolean;
  // View-only slot for the volunteers page — grants read access to
  // the applications pipeline (all statuses) without the write
  // path. Anyone with `can_manage_volunteering` implicitly has view
  // access too.
  can_view_volunteering: boolean;
  // Granular approval flags — an operator can hold one, both, or
  // neither. Full can_manage_volunteering implies BOTH. The DB
  // trigger `volunteers_enforce_transition` enforces the same rule
  // server-side so hiding a button in the UI is defense in depth,
  // not the only gate.
  can_approve_volunteering_coordinator: boolean;
  can_approve_volunteering_hr: boolean;
  can_manage_donations: boolean;
  can_manage_payment_methods: boolean;
  can_manage_chat: boolean;
};

const NO_PERMS: Permissions = {
  is_super_admin: false,
  can_manage_home: false,
  can_manage_community: false,
  can_manage_volunteering: false,
  can_view_volunteering: false,
  can_approve_volunteering_coordinator: false,
  can_approve_volunteering_hr: false,
  can_manage_donations: false,
  can_manage_payment_methods: false,
  can_manage_chat: false,
};

export type EffectivePermissions = Permissions & {
  canHome: boolean;
  canCommunity: boolean;
  canVolunteering: boolean;   // manage — full write access
  canViewVolunteering: boolean; // read — access the page but no edits
  canApproveVolunteeringCoordinator: boolean; // REVIEW → HR / reject
  canApproveVolunteeringHr: boolean; // HR → ACCEPTED / reject
  canDonations: boolean;
  canPaymentMethods: boolean;
  canChat: boolean;
};

function derive(perms: Permissions): EffectivePermissions {
  const canManageVolunteering = perms.is_super_admin || perms.can_manage_volunteering;
  return {
    ...perms,
    canHome: perms.is_super_admin || perms.can_manage_home,
    canCommunity: perms.is_super_admin || perms.can_manage_community,
    canVolunteering: canManageVolunteering,
    // A manager also implicitly counts as a viewer, and any
    // explicit view-only grant — or either approval permission —
    // opens the same door.
    canViewVolunteering:
      canManageVolunteering
      || perms.can_view_volunteering
      || perms.can_approve_volunteering_coordinator
      || perms.can_approve_volunteering_hr,
    canApproveVolunteeringCoordinator:
      canManageVolunteering || perms.can_approve_volunteering_coordinator,
    canApproveVolunteeringHr:
      canManageVolunteering || perms.can_approve_volunteering_hr,
    canDonations: perms.is_super_admin || perms.can_manage_donations,
    canPaymentMethods: perms.is_super_admin || perms.can_manage_payment_methods,
    canChat: perms.is_super_admin || perms.can_manage_chat,
  };
}

export type VolunteerIdentity = {
  volunteer_id: number;
  full_name: string;
  application_status: 'REVIEW' | 'HR' | 'ACCEPTED' | 'REJECTED';
};

type AuthState = {
  session: Session | null;
  isAdmin: boolean | null;
  isVolunteer: boolean | null;
  volunteer: VolunteerIdentity | null;
  permissions: EffectivePermissions;
  loading: boolean;
  // True whenever the session is aal1 but the account has a verified
  // TOTP factor — App.tsx uses this to gate the dashboard behind
  // MfaChallengePage. mfaChecked is false until the first AAL probe
  // resolves; App.tsx renders a spinner while it's false so a
  // freshly signed-in user doesn't flash the dashboard for one frame.
  mfaRequired: boolean;
  mfaChecked: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isVolunteer, setIsVolunteer] = useState<boolean | null>(null);
  const [volunteer, setVolunteer] = useState<VolunteerIdentity | null>(null);
  const [permissions, setPermissions] = useState<EffectivePermissions>(derive(NO_PERMS));
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaChecked, setMfaChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Track AAL — the assurance level Supabase issued for this session.
  // If the account has a verified factor (nextLevel = aal2) but the
  // session is only aal1, App.tsx swaps the dashboard for the deck
  // challenge until the code is entered.
  useEffect(() => {
    let cancelled = false;
    if (!session) {
      setMfaRequired(false);
      setMfaChecked(true);
      return;
    }
    setMfaChecked(false);
    void supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        setMfaRequired(false);
        setMfaChecked(true);
        return;
      }
      const need = data.currentLevel === 'aal1' && data.nextLevel === 'aal2';
      setMfaRequired(need);
      setMfaChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [session]);

  const loadPermissions = async () => {
    if (!session) {
      setIsAdmin(null);
      setIsVolunteer(null);
      setVolunteer(null);
      setPermissions(derive(NO_PERMS));
      return;
    }
    const [adminRes, volRes] = await Promise.all([
      supabase
        .from('app_admins')
        .select('is_super_admin, can_manage_home, can_manage_community, can_manage_volunteering, can_view_volunteering, can_approve_volunteering_coordinator, can_approve_volunteering_hr, can_manage_donations, can_manage_payment_methods, can_manage_chat')
        .eq('user_id', session.user.id)
        .maybeSingle(),
      supabase
        .from('volunteers')
        .select('id, full_name, application_status')
        .eq('user_id', session.user.id)
        .maybeSingle(),
    ]);

    if (adminRes.error) console.error('permissions lookup failed', adminRes.error);
    if (volRes.error) console.error('volunteer lookup failed', volRes.error);

    setIsAdmin(Boolean(adminRes.data));
    setPermissions(derive((adminRes.data ?? NO_PERMS) as Permissions));

    if (volRes.data) {
      setIsVolunteer(true);
      setVolunteer({
        volunteer_id: volRes.data.id as number,
        full_name: volRes.data.full_name as string,
        application_status: volRes.data.application_status as VolunteerIdentity['application_status'],
      });
    } else {
      setIsVolunteer(false);
      setVolunteer(null);
    }
  };

  useEffect(() => {
    void loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const signIn: AuthState['signIn'] = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(null);
    setIsVolunteer(null);
    setVolunteer(null);
    setPermissions(derive(NO_PERMS));
  };

  return (
    <AuthContext.Provider
      value={{ session, isAdmin, isVolunteer, volunteer, permissions, loading, mfaRequired, mfaChecked, signIn, signOut, refreshPermissions: loadPermissions }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
